import { useEffect, useRef, useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { useTranslation } from "react-i18next";
import { Languages, Check, X } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { LANGUAGE_STORAGE_KEY } from "../../i18n";

const LANGUAGES = [
  { code: "en", label: "English" },
  { code: "hi", label: "\u0939\u093f\u0928\u094d\u0926\u0940" },
  { code: "te", label: "\u0c24\u0c46\u0c32\u0c41\u0c17\u0c41" },
  { code: "kn", label: "\u0c95\u0ca8\u0ccd\u0ca8\u0ca1" },
];

export function LanguageSelector({ onPointerEnter, onPointerLeave, onDrawerOpenChange }) {
  const { i18n, t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [mobile, setMobile] = useState(() => window.matchMedia("(max-width: 639px)").matches);
  const openedByHover = useRef(false);
  const closeTimer = useRef(null);
  const cancelClose = () => window.clearTimeout(closeTimer.current);
  const changeOpen = (value) => {
    cancelClose();
    openedByHover.current = false;
    onDrawerOpenChange?.(mobile && value);
    setOpen(value);
  };

  useEffect(() => {
    const media = window.matchMedia("(max-width: 639px)");
    const update = () => setMobile(media.matches);
    media.addEventListener("change", update);
    return () => {
      media.removeEventListener("change", update);
      window.clearTimeout(closeTimer.current);
    };
  }, []);
  useEffect(() => {
    onDrawerOpenChange?.(mobile && open);
    return () => onDrawerOpenChange?.(false);
  }, [mobile, open, onDrawerOpenChange]);

  const enter = (event) => {
    cancelClose();
    onPointerEnter?.(event);
  };
  const leave = (event) => {
    onPointerLeave?.(event);
    if (mobile || event.pointerType !== "mouse") return;
    cancelClose();
    closeTimer.current = window.setTimeout(() => setOpen(false), 200);
  };
  const language = (i18n.resolvedLanguage || "en").split("-")[0];
  const options = (
    <div className="divide-y divide-border">
      {LANGUAGES.map(({ code, label }) => (
        <button key={code} type="button" lang={code} aria-pressed={language === code}
          onClick={() => {
            i18n.changeLanguage(code);
            localStorage.setItem(LANGUAGE_STORAGE_KEY, code);
            changeOpen(false);
          }}
          className="flex min-h-11 w-full items-center justify-between gap-3 px-3 py-2 text-left text-sm hover:bg-muted focus-visible:bg-muted focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary">
          <span>{label}</span>
          {language === code && <Check className="h-4 w-4" aria-hidden="true" />}
        </button>
      ))}
    </div>
  );
  const trigger = (
    <button type="button" aria-label={t("language.label")}
      onPointerEnter={(event) => {
        enter(event);
        if (!mobile && event.pointerType === "mouse" && !open) {
          openedByHover.current = true;
          setOpen(true);
        }
      }}
      onPointerLeave={leave}
      onClick={(event) => {
        if (open && openedByHover.current) {
          event.preventDefault();
          openedByHover.current = false;
        }
      }}
      className="flex w-full items-center gap-3 px-3 py-2 text-left text-sm hover:bg-muted focus-visible:bg-muted focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary">
      <Languages className="h-4 w-4 shrink-0" aria-hidden="true" />
      <span>{t("language.label")}</span>
    </button>
  );

  if (mobile) {
    return (
      <Dialog.Root open={open} onOpenChange={changeOpen}>
        <Dialog.Trigger asChild>{trigger}</Dialog.Trigger>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 z-[60] bg-black/40" />
          <Dialog.Content aria-describedby={undefined}
            className="fixed inset-x-0 bottom-0 z-[61] max-h-[80dvh] overflow-y-auto rounded-t-2xl border border-border bg-popover p-4 pb-[max(1rem,env(safe-area-inset-bottom))] text-popover-foreground shadow-xl">
            <div className="mb-3 flex items-center justify-between">
              <Dialog.Title className="text-base font-semibold">{t("language.label")}</Dialog.Title>
              <Dialog.Close aria-label={t("common.close", { defaultValue: "Close" })}
                className="grid h-11 w-11 place-items-center rounded-full hover:bg-muted">
                <X className="h-5 w-5" aria-hidden="true" />
              </Dialog.Close>
            </div>
            {options}
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    );
  }

  return (
    <Popover open={open} onOpenChange={changeOpen}>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent data-user-menu-surface side="left" align="start" collisionPadding={12}
        className="w-44 max-w-[calc(100vw-24px)] p-1" aria-label={t("language.label")}
        onPointerEnter={enter} onPointerLeave={leave}
        onOpenAutoFocus={(event) => {
          if (openedByHover.current) event.preventDefault();
        }}
        onCloseAutoFocus={(event) => {
          if (openedByHover.current) event.preventDefault();
        }}>
        {options}
      </PopoverContent>
    </Popover>
  );
}